/**
 * The legal name and address a freelancer saved on the onboarding's Legal Name
 * and Address step, kept in this browser until they sign, so a reload on the
 * signing step returns them there with their details filled in. Keyed by their
 * contract, so another freelancer on the same browser never sees them. Nothing
 * is sent anywhere until they sign: the signature carries the details.
 *
 * Storage can be unavailable (private windows, blocked site data); every call
 * then does nothing, and the freelancer simply enters their details again.
 */

const PARTY_KEYS = ['legal_name', 'street', 'city', 'region', 'postal_code'];

/** @param {string} contractId */
export function draftKey(contractId) {
    return `d3assets.msa-draft.${contractId}`;
}

/**
 * @param {Storage | undefined} storage
 * @param {string} contractId
 * @returns {Record<string, string> | null} the saved parties, or null for none
 */
export function loadDraft(storage, contractId) {
    try {
        const raw = storage?.getItem(draftKey(contractId));
        if (!raw) return null;
        const saved = JSON.parse(raw);
        return Object.fromEntries(
            PARTY_KEYS.map((k) => [k, typeof saved[k] === 'string' ? saved[k] : '']),
        );
    } catch {
        return null;
    }
}

/**
 * @param {Storage | undefined} storage
 * @param {string} contractId
 * @param {Record<string, string>} parties
 */
export function saveDraft(storage, contractId, parties) {
    try {
        storage?.setItem(
            draftKey(contractId),
            JSON.stringify(Object.fromEntries(PARTY_KEYS.map((k) => [k, parties[k] ?? '']))),
        );
    } catch {
        // Unavailable storage only means the details aren't kept across a reload.
    }
}

/**
 * @param {Storage | undefined} storage
 * @param {string} contractId
 */
export function clearDraft(storage, contractId) {
    try {
        storage?.removeItem(draftKey(contractId));
    } catch {
        // Nothing was kept, so there is nothing to clear.
    }
}
